import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faFilePdf, 
  faFileAlt, 
  faSpinner,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import FileViewerModal from '../common/FileViewerModal';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import TopBar from '../TopBar';
import styles from '../common/EquipmentDetails.module.css';

const PublicEquipmentCardDetails = () => {
  const { userId, category, itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the userName from location state if available
  const backPath = location.state?.backPath || `/${location.state?.userName || ''}`;

  // State for data structure
  const [groupData, setGroupData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tours, setTours] = useState([]); // For RightMenu

  // File viewer state
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [fileViewerUrl, setFileViewerUrl] = useState('');
  const [fileViewerType, setFileViewerType] = useState('');
  const [fileViewerName, setFileViewerName] = useState('');

  // Check for screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Get the formatted display name for the category
  const getCategoryDisplayName = (categoryId) => {
    const categories = {
      'audioEquipment': 'Audio Equipment',
      'backlineEquipment': 'Backline Equipment',
      'lightingEquipment': 'Lighting Equipment',
      'visualEquipment': 'Visual Equipment',
      'pyroEquipment': 'Pyro Equipment',
      'stagingEquipment': 'Staging Equipment',
      'miscellaneousEquipment': 'Miscellaneous Equipment'
    };
    return categories[categoryId] || 'Equipment';
  };

  // Fetch the item details
  useEffect(() => {
    const fetchItemDetails = async () => {
      if (!userId || !category || !itemId) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        const db = getFirestoreInstance();
        
        // Get from inventoryGroups collection
        const groupRef = doc(db, 'users', userId, 'inventoryGroups', itemId);
        let groupSnap = await getDoc(groupRef);
        
        if (groupSnap.exists()) {
          // Set group data
          const groupData = {
            id: groupSnap.id,
            ...groupSnap.data()
          };
          
          setGroupData(groupData);
        } else {
          setError('Equipment item not found');
        }
      } catch (error) {
        console.error('Error fetching equipment details:', error);
        setError('Failed to load equipment details');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchItemDetails();
  }, [userId, category, itemId]);

  // Handle back button click
  const handleBack = () => {
    navigate(backPath);
  };

  // Handle view file
  const handleViewFile = (item) => {
    if (item.fileUrl) {
      setFileViewerUrl(item.fileUrl);
      setFileViewerName(item.name || getFileName(item.fileUrl));
      
      // Detect file type from URL extension
      const fileUrl = item.fileUrl.toLowerCase();
      if (fileUrl.endsWith('.pdf')) {
        setFileViewerType('application/pdf');
      } else if (fileUrl.match(/\.(jpe?g|png|gif|bmp|webp)$/)) {
        // Use the specific image type rather than generic 'image'
        const extension = fileUrl.split('.').pop();
        setFileViewerType(`image/${extension === 'jpg' ? 'jpeg' : extension}`);
      } else if (fileUrl.match(/\.(docx?)$/)) {
        setFileViewerType('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      } else if (fileUrl.match(/\.(xlsx?)$/)) {
        setFileViewerType('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      } else {
        setFileViewerType('application/octet-stream');
      }
      
      setShowFileViewer(true);
    }
  };

  // Extract filename from fileUrl if present
  const getFileName = (url) => {
    if (!url) return 'Document';
    
    try {
      const parts = url.split('/');
      // Get the last part of the URL (filename)
      const rawFilename = parts[parts.length - 1];
      // Decode any URL encoded characters
      return decodeURIComponent(rawFilename);
    } catch (error) {
      console.error('Error extracting filename:', error);
      return 'Document';
    }
  };

  // Handle tour selection (for RightMenu)
  const handleTourSelect = (tour) => {
    // Not needed for public view, but included for consistency
  };

  // Overview content
  const renderOverview = () => {
    // Use groupData for overview
    if (!groupData) return null;
    
    return (
      <div className={styles.equipmentDetails}>
        <div className={styles.imageSection}>
          <div className={styles.imageContainer}>
            {groupData.coverPhotoUrl ? (
              <img 
                src={groupData.coverPhotoUrl} 
                alt={groupData.name} 
                className={styles.equipmentImage} 
              />
            ) : (
              <div className={styles.placeholderImage}>
                <span>{groupData.name.charAt(0)}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className={styles.infoSection}>
          <div className={styles.infoHeader}>
            <h1 className={styles.equipmentName}>{groupData.name}</h1>
            <div className={styles.categoryBadge}>
              {groupData.inventoryType || getCategoryDisplayName(category)}
            </div>
          </div>
          
          <div className={styles.basicInfo}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Type:</span>
              <span className={styles.infoValue}>{groupData.type}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Sub Type:</span>
              <span className={styles.infoValue}>{groupData.subType}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Brand:</span>
              <span className={styles.infoValue}>{groupData.brand}</span>
            </div>
            {groupData.model && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Model:</span>
                <span className={styles.infoValue}>{groupData.model}</span>
              </div>
            )}
            
            <div className={styles.quantityInfo}>
              <span className={styles.quantityLabel}>Available Quantity</span>
              <span className={styles.quantityValue}>
                <span className={styles.totalQuantity}>{groupData.availableQuantity || 0}</span>
              </span>
            </div>
            <div className={styles.priceInfo}>
              <span className={styles.priceLabel}>Rent Per Day</span>
              <span className={styles.priceValue}>₹{groupData.rentPerDay}</span>
            </div>
          </div>
          
          {groupData.description && (
            <div className={styles.descriptionSection}>
              <h3 className={styles.sectionTitle}>Description</h3>
              <p className={styles.descriptionText}>{groupData.description}</p>
            </div>
          )}
          
          {groupData.fileUrl && (
            <div className={styles.attachmentSection}>
              <h3 className={styles.sectionTitle}>Attachment</h3>
              <button 
                className={styles.attachmentButton}
                onClick={() => handleViewFile(groupData)}
              >
                <FontAwesomeIcon 
                  icon={groupData.fileUrl.toLowerCase().endsWith('.pdf') ? faFilePdf : faFileAlt} 
                  className={styles.attachmentIcon}
                />
                <span className={styles.attachmentName}>
                  {getFileName(groupData.fileUrl)}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Content to display based on state
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
          <p>Loading equipment details...</p>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className={styles.errorContainer}>
          <FontAwesomeIcon icon={faFileAlt} className={styles.errorIcon} />
          <h2>Error</h2>
          <p>{error}</p>
          <button className={styles.backButton} onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Profile</span>
          </button>
        </div>
      );
    }
    
    if (!groupData) {
      return (
        <div className={styles.notFoundContainer}>
          <FontAwesomeIcon icon={faFileAlt} className={styles.notFoundIcon} />
          <h2>Item Not Found</h2>
          <p>The requested equipment item could not be found.</p>
          <button className={styles.backButton} onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Profile</span>
          </button>
        </div>
      );
    }

    return (
      <div className={styles.detailsContainer}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Profile</span>
          </button>
        </div>
        
        {/* Render overview content */}
        {renderOverview()}
      </div>
    );
  };

  return (
    <div className={styles.inventoryContainer}>
      <div className={styles['main-layout']}>
        {!isMobile && <LeftMenu />}
        <main className={styles['equipment-details-main']}>
          {renderContent()}
        </main>
        {!isMobile && <RightMenu tours={tours} onTourSelect={handleTourSelect} />}
      </div>
      
      {/* File Viewer Modal */}
      {groupData && (
        <FileViewerModal
          isOpen={showFileViewer}
          onClose={() => setShowFileViewer(false)}
          file={{
            fileUrl: fileViewerUrl,
            fileType: fileViewerType,
            fileName: fileViewerName || 'Document'
          }}
        />
      )}
    </div>
  );
};

export default PublicEquipmentCardDetails;